import MetaHead from "components/base/MetaHead";
import { useRouter } from "next/router";
import { Flex } from "@chakra-ui/react";
import { useMemo } from "react";
import CustomImage from "components/base/Image";
import {
  MotionBox as Box,
  MotionContainer as Container,
  MotionStack as Stack,
  MotionGrid as Grid,
  staggeredAppear,
  appearBelow,
} from "utils/motion";
import { allowedLocales } from "translations";
import BlogRelatedSection from "components/sections/BlogRelatedSection";
import MainContainer from "components/sections/MainContainer";
import Text from "components/base/Text";
import RichText from "components/base/RichText";
import RichHeader from "components/base/RichHeader";
import Link from "components/base/Link";
import Date from "components/blog/Date";
import SocialLink from "components/base/SocialLink";
import Facebook from "components/icons/social/Facebook";
import LinkedIn from "components/icons/social/LinkedIn";
import Twitter from "components/icons/social/Twitter";
import ArrowBack from "components/icons/ArrowBack";
import YoutubeEmbed from "components/base/YoutubeEmbed";
import FloatingCTA from "components/blocks/FloatingCTA";
import PreviewBanner from "components/base/PreviewBanner";

import { getPost, getPostRaw, transformArticle } from "lib/payloadApi";
import { useLivePreview } from "@payloadcms/live-preview-react";

function renderSlice(block, index) {
  switch (block.blockType) {
    case "text":
      return (
        <Stack key={`key${index}`} spacing={0} px={[0, null, 4, null, null]}>
          <Box py={["sm", null, "md"]}>
            <RichText content={block.content} color="black.700" />
          </Box>
        </Stack>
      );
    case "youtubeEmbed":
      return (
        <YoutubeEmbed
          mx="auto"
          w="full"
          id={block.youtubeId}
          key={`key${index}`}
        />
      );
    case "image": {
      const imageUrl = block.image?.url;
      const imageDimensions = block.image?.dimensions;

      const aspectRatio = imageDimensions
        ? (imageDimensions.height / imageDimensions.width) * 100
        : 56.25;

      return (
        <Box
          key={`key${index}`}
          w="full"
          position="relative"
          paddingBottom={`${aspectRatio}%`}
          height={0}
        >
          <CustomImage
            src={imageUrl}
            alt={block.image?.alt || ""}
            w="full"
            h="full"
            objectFit="contain"
            style={{ position: "absolute", top: 0, left: 0 }}
          />
        </Box>
      );
    }
    default:
      return null;
  }
}

function getPostData(data) {
  return {
    featureImage: data.feature_image,
    introduction: data.introduction,
    featureVideo: data.feature_video,
    featureVideoPosition: data.feature_video_position,
    date: data.published_date,
    lastUpdateDate: data.last_publication_date,
    title: data.title,
    body: data.body,
    metaDescription: data.meta_description,
    metaTitle: data.meta_title,
    relatedArticles: data.related_articles,
  };
}

export default function Post({ post, rawPost, preview }) {
  const router = useRouter();
  const { locale } = router;

  const { data: liveRawData } = useLivePreview({
    serverURL: process.env.NEXT_PUBLIC_PAYLOAD_URL || "http://localhost:3080",
    depth: 2,
    initialData: rawPost || {},
  });

  const livePostData = useMemo(() => {
    if (preview && liveRawData && liveRawData.id) {
      try {
        return getPostData(transformArticle(liveRawData).data);
      } catch {
        return null;
      }
    }
    return null;
  }, [preview, liveRawData]);

  // Derive from props instead of copying them into state: on client-side
  // navigation (e.g. related articles) Next reuses this component and only
  // updates `post`, which state would ignore.
  const ssrData = getPostData(post.data);

  const {
    featureImage,
    introduction,
    featureVideo,
    featureVideoPosition,
    date,
    lastUpdateDate,
    title,
    body,
    metaDescription,
    metaTitle,
    relatedArticles,
  } = livePostData || ssrData;

  const pageUrl = encodeURIComponent(
    `https://www.hillhelicopters.com/${locale}/blog/${post.uid}`,
  );
  const pageContent = encodeURIComponent(metaDescription || "");
  const metaImageUrl =
    featureImage && featureImage.url ? featureImage.url : null;
  const headTitle =
    metaTitle || (title && title[0] && title[0].text ? title[0].text : "");

  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.hillhelicopters.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://www.hillhelicopters.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: headTitle,
      },
    ],
  };

  const defaultArticleSchema = {
    "@type": "Article",
    headline: headTitle,
    description: metaDescription || "",
    datePublished: date || "",
    dateModified: lastUpdateDate || date || "",
    author: {
      "@type": "Organization",
      name: "Hill Helicopters",
      url: "https://www.hillhelicopters.com",
    },
    publisher: {
      "@type": "Organization",
      name: "Hill Helicopters",
      logo: {
        "@type": "ImageObject",
        url: "https://www.hillhelicopters.com/images/favicon-256.png",
      },
    },
    ...(metaImageUrl ? { image: metaImageUrl } : {}),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.hillhelicopters.com/blog/${post.uid}`,
    },
  };

  const resolvedStructuredData = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [breadcrumbSchema, defaultArticleSchema],
  });

  return (
    <MainContainer navStyle={{ gradient: false, inverted: true }} bg="white">
      <MetaHead
        title={headTitle}
        keywords={["LBL_BLOG_META_TITLE"]}
        description={metaDescription}
        imageUrl={metaImageUrl}
        imageDimensions={
          featureImage && featureImage.dimensions
            ? {
                width: featureImage.dimensions.width,
                height: featureImage.dimensions.height,
              }
            : null
        }
        structuredData={resolvedStructuredData}
        ogType="article"
      />
      <Container
        maxWidth="xl"
        p={0}
        bg="white"
        m="auto"
        variants={staggeredAppear}
        initial="hidden"
        animate="visible"
        align="center"
      >
        <Grid
          templateColumns={["1fr", null, "auto 1fr", null, null]}
          px={["md", null, "xxl"]}
          pt={["60px", null, 0]}
          pb={["md", null, "xl"]}
          gap={[0, null, 12]}
        >
          <Link
            href="/blog"
            _focus={{ boxShadow: "none" }}
            style={{ textAlign: "left" }}
          >
            <ArrowBack
              position="relative"
              left="10px"
              transition="all 300ms"
              _hover={{ cursor: "pointer", left: "0" }}
              mt={6}
            />
          </Link>
          <Box>
            <Text
              as="h1"
              type="headline_md"
              textAlign="left"
              variants={appearBelow}
              color="black.700"
            >
              {title[0].text}
            </Text>
            {introduction?.root?.children?.length > 0 && (
              <Box
                mt={2}
                variants={appearBelow}
                textAlign="left"
                color="black.700"
              >
                <RichHeader content={introduction} />
              </Box>
            )}
            <Date date={date} mt={4} textAlign="left" variants={appearBelow} />
          </Box>
        </Grid>

        {featureVideo && featureVideoPosition !== "bottom" && (
          <Box bg="black.900" w="80%">
            <YoutubeEmbed
              w="full"
              h="auto"
              id={featureVideo}
              variants={appearBelow}
            />
          </Box>
        )}
        {(!featureVideo || featureVideo === "") &&
          featureImage &&
          featureImage.url && (
            <CustomImage
              src={featureImage.url}
              alt={featureImage.alt || headTitle || ""}
              w="full"
              h="auto"
              objectFit="cover"
            />
          )}

        <Grid
          variants={appearBelow}
          templateColumns={["1fr", null, "auto 1fr", null, null]}
          maxWidth="900px"
          px={["md", null, "xxl"]}
          pb={[0, null, "xxl"]}
          gap={[0, null, 4, null, null]}
        >
          <Flex direction={["row", null, "column"]} py={[0, null, "lg"]}>
            <SocialLink
              href={`https://twitter.com/intent/tweet?url=${pageUrl}&text=${pageContent}`}
              my={[0, null, 1]}
              mr={[2, null, 0]}
            >
              <Twitter width="32px" height="32px" color="white" bg="gray.800" />
            </SocialLink>
            <SocialLink
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${pageUrl}`}
              my={[0, null, 1]}
              mr={[2, null, 0]}
            >
              <LinkedIn width="32px" height="32px" color="gray.800" />
            </SocialLink>
            <SocialLink
              href={`https://www.facebook.com/sharer.php?u=${pageUrl}`}
              my={[0, null, 1]}
              mr={[2, null, 0]}
            >
              <Facebook width="32px" height="32px" color="gray.800" />
            </SocialLink>
          </Flex>
          <Stack spacing={[0, null, 16]} w="full">
            <Box color="black.700" textAlign="left">
              {body.map((slice, index) => renderSlice(slice, index))}
            </Box>
          </Stack>
        </Grid>
        {featureVideo && featureVideoPosition === "bottom" && (
          <Box bg="black.900" w={["90%", "90%", "80%"]} mb={[24, 24, 0]}>
            <YoutubeEmbed
              w="full"
              h="auto"
              id={featureVideo}
              variants={appearBelow}
            />
          </Box>
        )}
      </Container>
      <BlogRelatedSection relatedArticles={relatedArticles} />
      <FloatingCTA />
      {preview && <PreviewBanner />}
    </MainContainer>
  );
}

export async function getStaticProps({ params, locale, preview }) {
  const selectedLocale = allowedLocales.indexOf(locale) > -1 ? locale : null;

  let post = null;
  let rawPost = null;
  try {
    post = await getPost(params.uid, selectedLocale, null, preview);
    if (preview) {
      rawPost = await getPostRaw(params.uid, selectedLocale, true);
    }
    // eslint-disable-next-line no-unused-vars
  } catch (error) {
    post = null;
  }

  if (!post) {
    try {
      post = await getPost(params.uid, null, null, preview);
      if (preview) {
        rawPost = await getPostRaw(params.uid, null, true);
      }
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      post = null;
    }
  }

  if (!post) {
    return { notFound: true, revalidate: 60 };
  }

  return {
    props: { post, rawPost, preview: preview || false },
    revalidate: 60,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}
